import ThemeToggle from "../components/ThemeToggle";
import SecureImage from "../components/SecureImage";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "aws-amplify/auth";
import axios from "axios";
import { formatDate, formatRelative } from "../utils/dateFormat";
import { adminGetUsers, adminVerifyPro, adminSuspendUser, updateLeadPricing, getLeadPricing, getAuthHeader, getDisputes, resolveDispute, getTickets, updateTicket, getAnalytics, getServices, addService, updateService, deleteService, getAdminFlags, adminFlagAction, getWsToken } from "../api";
import { awsConfig } from "../aws-config";

interface User {
  sub:     string;
  email:   string;
  role:    string;
  status:  string;
  enabled: boolean;
  created: string;
}

const NAV = ["Dashboard", "Users", "Pricing", "Support", "Disputes", "Settings"];


// ── Premium Expandable Category System ──────────────────
function SettingsTab({ services, servicesLoading, newCategoryName, setNewCategoryName,
  newCategoryIcon, setNewCategoryIcon, newSubcategories, setNewSubcategories,
  handleAddService, handleDeleteService, updateService, loadServicesAdmin }: any) {

  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<{id: string, value: string} | null>(null);
  const [editingSub, setEditingSub] = useState<{id: string, idx: number, value: string} | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);

  function showSaved(msg: string) {
    setSavedMsg(msg);
    setTimeout(() => setSavedMsg(null), 2000);
  }

  async function saveCategoryName(svc: any) {
    if (!editingName) return;
    try {
      await updateService(svc.categoryId, { ...svc, name: editingName.value });
      showSaved("Category renamed");
    } catch {}
    setEditingName(null);
    loadServicesAdmin();
  }

  async function saveSubcategory(svc: any) {
    if (!editingSub) return;
    const newSubs = [...(svc.subcategories || [])];
    newSubs[editingSub.idx] = editingSub.value;
    try {
      await updateService(svc.categoryId, { ...svc, subcategories: newSubs });
      showSaved("Saved ✓");
    } catch {}
    setEditingSub(null);
    loadServicesAdmin();
  }

  async function deleteSubcategory(svc: any, idx: number) {
    const newSubs = svc.subcategories.filter((_: any, i: number) => i !== idx);
    try {
      await updateService(svc.categoryId, { ...svc, subcategories: newSubs });
      showSaved("Removed");
      loadServicesAdmin();
    } catch {}
  }

  async function addSubcategory(svc: any, newSub: string) {
    if (!newSub.trim()) return;
    const newSubs = [...(svc.subcategories || []), newSub.trim()];
    try {
      await updateService(svc.categoryId, { ...svc, subcategories: newSubs });
      showSaved("Added ✓");
      loadServicesAdmin();
    } catch {}
  }

  const filtered = services.filter((s: any) =>
    searchQuery === "" ||
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (s.subcategories || []).some((sub: string) => sub.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold text-navy">Service Categories</h1>
          <p className="text-sm text-gray-400">{services.length} categories · {services.reduce((sum: number, s: any) => sum + (s.subcategories?.length || 0), 0)} subcategories</p>
        </div>
        <div className="flex items-center gap-2">
          {savedMsg && (
            <span className="text-xs text-teal font-bold bg-teal-light rounded-full px-3 py-1 animate-fade-in">{savedMsg}</span>
          )}
          <button onClick={loadServicesAdmin} className="bg-blue-pale text-mouna-blue text-xs font-bold px-4 py-2 rounded-full hover:bg-mouna-blue hover:text-white transition-colors">↻ Refresh</button>
          <button onClick={() => setShowAddForm(!showAddForm)}
            className="bg-mouna-blue text-white text-xs font-bold px-4 py-2 rounded-full">
            {showAddForm ? "✕ Cancel" : "+ Add Category"}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
        <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search categories and subcategories..."
          className="w-full bg-white border border-gray-200 rounded-2xl pl-10 pr-4 py-3 text-sm outline-none focus:border-mouna-blue transition-colors shadow-card"/>
        {searchQuery && (
          <button onClick={() => setSearchQuery("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">✕</button>
        )}
      </div>

      {/* Add form */}
      {showAddForm && (
        <div className="bg-white rounded-2xl p-5 shadow-card mb-4 border-2 border-mouna-blue animate-slide-up">
          <div className="font-bold text-navy mb-3">New Category</div>
          <div className="flex gap-2 mb-3">
            <input value={newCategoryIcon} onChange={e => setNewCategoryIcon(e.target.value)}
              placeholder="🔨" className="w-14 border border-gray-200 rounded-xl px-2 py-2.5 text-sm outline-none text-center text-xl"/>
            <input value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)}
              placeholder="Category name..." autoFocus
              className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-mouna-blue"/>
          </div>
          <input value={newSubcategories} onChange={e => setNewSubcategories(e.target.value)}
            placeholder="Subcategories: Small, Medium, Large"
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-mouna-blue mb-3"/>
          <button onClick={() => { handleAddService(); setShowAddForm(false); }}
            className="btn btn-primary btn-full">Create Category</button>
        </div>
      )}

      {/* Loading */}
      {servicesLoading && (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-4 border-mouna-blue border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      )}

      {/* Category cards */}
      <div className="space-y-2">
        {filtered.map((svc: any) => {
          const isOpen = openCategory === svc.categoryId;
          return (
            <div key={svc.categoryId}
              className={"bg-white rounded-2xl shadow-card overflow-hidden transition-all " + (isOpen ? "ring-2 ring-mouna-blue ring-opacity-30" : "")}>

              {/* Category header */}
              <div className="flex items-center gap-3 px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setOpenCategory(isOpen ? null : svc.categoryId)}>
                <div className="w-10 h-10 bg-blue-pale rounded-xl flex items-center justify-center text-xl flex-shrink-0">
                  {svc.icon || "🔨"}
                </div>
                <div className="flex-1 min-w-0">
                  {editingName?.id === svc.categoryId ? (
                    <input autoFocus value={editingName?.value || ""}
                      onChange={e => setEditingName({id: svc.categoryId, value: e.target.value})}
                      onBlur={() => saveCategoryName(svc)}
                      onKeyDown={e => { if (e.key === "Enter") saveCategoryName(svc); if (e.key === "Escape") setEditingName(null); }}
                      onClick={e => e.stopPropagation()}
                      className="font-bold text-navy text-sm border-b-2 border-mouna-blue outline-none bg-transparent w-full"/>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-navy">{svc.name}</span>
                      <button onClick={e => { e.stopPropagation(); setEditingName({id: svc.categoryId, value: svc.name}); }}
                        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-mouna-blue text-xs transition-opacity">✏️</button>
                    </div>
                  )}
                  <div className="text-xs text-gray-400 mt-0.5">{(svc.subcategories || []).length} subcategories</div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={e => { e.stopPropagation(); setEditingName({id: svc.categoryId, value: svc.name}); }}
                    className="text-gray-400 hover:text-mouna-blue text-xs p-1.5 hover:bg-blue-pale rounded-lg transition-colors">✏️</button>
                  <button onClick={e => { e.stopPropagation(); if(window.confirm("Delete " + svc.name + "?")) handleDeleteService(svc.categoryId); }}
                    className="text-red-400 hover:text-red-600 text-xs p-1.5 hover:bg-red-50 rounded-lg transition-colors">🗑️</button>
                  <span className={"text-gray-400 transition-transform duration-200 " + (isOpen ? "rotate-180" : "")}>▾</span>
                </div>
              </div>

              {/* Expanded subcategories */}
              {isOpen && (
                <div className="border-t border-gray-100 px-5 py-3 animate-fade-in">
                  <div className="text-xs text-gray-400 uppercase tracking-wider mb-3 font-bold">Subcategories / Job Sizes</div>
                  <div className="space-y-1.5 mb-3">
                    {(svc.subcategories || []).map((sub: string, idx: number) => (
                      <div key={idx} className="flex items-center gap-2 group">
                        <div className="w-1.5 h-1.5 rounded-full bg-mouna-blue opacity-50 flex-shrink-0"></div>
                        {editingSub?.id === svc.categoryId && editingSub?.idx === idx ? (
                          <input autoFocus value={editingSub.value}
                            onChange={e => setEditingSub({id: svc.categoryId, idx, value: e.target.value})}
                            onBlur={() => saveSubcategory(svc)}
                            onKeyDown={e => { if (e.key === "Enter") saveSubcategory(svc); if (e.key === "Escape") setEditingSub(null); }}
                            className="flex-1 border-b border-mouna-blue outline-none text-sm text-navy bg-transparent py-0.5"/>
                        ) : (
                          <span className="flex-1 text-sm text-navy py-1">{sub}</span>
                        )}
                        <button onClick={() => setEditingSub({id: svc.categoryId, idx, value: sub})}
                          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-mouna-blue text-xs transition-opacity p-1">✏️</button>
                        <button onClick={() => deleteSubcategory(svc, idx)}
                          className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 text-xs transition-opacity p-1">✕</button>
                      </div>
                    ))}
                  </div>

                  {/* Add subcategory inline */}
                  <AddSubcategoryInput onAdd={(val: string) => addSubcategory(svc, val)} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && !servicesLoading && (
        <div className="text-center py-12 text-gray-400">
          <div className="text-3xl mb-2">🔍</div>
          <div className="text-sm">No categories found</div>
        </div>
      )}
    </div>
  );
}

function AddSubcategoryInput({ onAdd }: { onAdd: (val: string) => void }) {
  const [value, setValue] = useState("");
  const [focused, setFocused] = useState(false);
  return (
    <div className={"flex items-center gap-2 border border-dashed rounded-xl px-3 py-2 transition-colors " + (focused ? "border-mouna-blue bg-blue-pale" : "border-gray-200")}>
      <span className="text-gray-400 text-sm">+</span>
      <input value={value} onChange={e => setValue(e.target.value)}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        onKeyDown={e => { if (e.key === "Enter" && value.trim()) { onAdd(value.trim()); setValue(""); } }}
        placeholder="Add subcategory..."
        className="flex-1 text-sm outline-none bg-transparent text-navy placeholder-gray-400"/>
      {value.trim() && (
        <button onClick={() => { onAdd(value.trim()); setValue(""); }}
          className="text-mouna-blue text-xs font-bold">Add</button>
      )}
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [activeNav, setActiveNav] = useState("Dashboard");
  const [users, setUsers]         = useState<User[]>([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const [actionMsg, setActionMsg] = useState("");
  const [supportConvos, setSupportConvos] = useState<any[]>([]);
  const [disputes, setDisputes] = useState<any[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [services, setServices] = useState<any[]>([]);
  const [flags, setFlags] = useState<any[]>([]);
  const [flagsLoading, setFlagsLoading] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState<any>(null);
  const [flagNote, setFlagNote] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryIcon, setNewCategoryIcon] = useState("🔨");
  const [newSubcategories, setNewSubcategories] = useState("");
  const [servicesLoading, setServicesLoading] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [tickets, setTickets] = useState<any[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [ticketReply, setTicketReply] = useState("");
  const ticketReplyRef = React.useRef<HTMLTextAreaElement>(null);
  const [estimatedTime, setEstimatedTime] = useState("within 24-48 hours");
  const [requestType, setRequestType] = useState("reply");
  const [resolvingDispute, setResolvingDispute] = useState<any | null>(null);
  const [disputeResolution, setDisputeResolution] = useState("");
  const [disputeNotes, setDisputeNotes] = useState("");
  const [activeConvo, setActiveConvo] = useState<string | null>(null);
  const [convoMessages, setConvoMessages] = useState<any[]>([]);
  const [supportReply, setSupportReply] = useState("");
  const adminWsRef = React.useRef<WebSocket | null>(null);
  const adminId = (() => {
    const k = Object.keys(localStorage).find(key => key.includes("idToken"));
    if (!k) return "";
    const t = localStorage.getItem(k);
    if (!t) return "";
    try { return JSON.parse(atob(t.split(".")[1])).sub || ""; } catch { return ""; }
  })();
  const [adminWsToken, setAdminWsToken] = useState("");
  React.useEffect(() => {
    // Fresh token for the support-chat WebSocket; localStorage copies go
    // stale after 1h and wsConnect rejects them.
    getWsToken().then(setAdminWsToken).catch(() => {});
  }, []);
  const [pricing, setPricing] = useState<any[]>([]);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [editingPrice, setEditingPrice] = useState<{category: string, jobSize: string, price: number, description: string} | null>(null);

  async function handleSignOut() {
    await signOut();
    localStorage.clear();
    navigate("/login");
  }

  async function loadUsers() {
    setLoading(true);
    setError("");
    try {
      const data = await adminGetUsers();
      setUsers(data);
    } catch {
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (activeNav === "Users") loadUsers();
    if (activeNav === "Dashboard") { loadAnalytics(); loadFlags(); }
    if (activeNav === "Settings") loadServicesAdmin();
    if (activeNav === "Pricing") loadPricing();
    if (activeNav === "Support") loadSupportConvos();
    if (activeNav === "Disputes") { loadDisputes(); loadAdminTickets(); }
  }, [activeNav]);

  async function loadPricing() {
    setPricingLoading(true);
    try {
      const data = await getLeadPricing();
      setPricing(data || []);
    } catch {
      console.error("Failed to load pricing");
    } finally {
      setPricingLoading(false);
    }
  }

  React.useEffect(() => {
    if (activeNav === "Support" && adminId && adminWsToken) {
      const ws = new WebSocket(`${awsConfig.wsUrl}?token=${encodeURIComponent(adminWsToken)}`);
      adminWsRef.current = ws;
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.conversationId === activeConvo) {
          setConvoMessages(prev => [...prev, data]);
        }
        loadSupportConvos();
      };
      return () => ws.close();
    }
  }, [activeNav, adminId, adminWsToken, activeConvo]);

  async function loadAdminTickets() {
    try {
      const data = await getTickets();
      setTickets(data || []);
    } catch (err) { console.error(err); }
  }

  async function handleTicketReply(ticketId: string, status: string) {
    if (!ticketReply.trim() && requestType === "reply") return;
    try {
      await updateTicket(ticketId, {
        message: ticketReply || (requestType === "pro_response" ? "Please respond to this ticket." : "Please provide more information."),
        status,
        requestType: requestType !== "reply" ? requestType : undefined,
        estimatedResolutionTime: status === "escalated" ? estimatedTime : undefined
      });
      setTicketReply("");
      setRequestType("reply");
      await loadAdminTickets();
      setActionMsg(status === "resolved" ? "Ticket resolved!" : status === "escalated" ? "Ticket escalated" : "Reply sent");
      setTimeout(() => setActionMsg(""), 3000);
    } catch { setActionMsg("Failed to send reply"); }
  }

  async function loadDisputes() {
    try {
      const d = await getDisputes();
      setDisputes(d || []);
    } catch (err) { console.error(err); }
  }

  async function handleResolveDispute(status: string) {
    if (!resolvingDispute) return;
    try {
      await resolveDispute(resolvingDispute.disputeId, disputeResolution, status, disputeNotes);
      setResolvingDispute(null);
      setDisputeResolution("");
      setDisputeNotes("");
      loadDisputes();
      setActionMsg("Dispute resolved successfully");
      setTimeout(() => setActionMsg(""), 3000);
    } catch { setActionMsg("Failed to resolve dispute"); }
  }

  async function loadFlags() {
    setFlagsLoading(true);
    try {
      const data = await getAdminFlags();
      setFlags(data || []);
    } catch (err) { console.error(err); }
    finally { setFlagsLoading(false); }
  }

  async function handleFlagAction(flagId: string, proId: string, action: string) {
    try {
      await adminFlagAction(flagId, proId, action, flagNote);
      await loadFlags();
      setSelectedFlag(null);
      setFlagNote("");
      setActionMsg(`Action '${action}' applied`);
      setTimeout(() => setActionMsg(""), 3000);
    } catch (err) { console.error(err); }
  }

  async function loadServicesAdmin() {
    setServicesLoading(true);
    try {
      const data = await getServices();
      setServices(data || []);
    } catch (err) { console.error(err); }
    finally { setServicesLoading(false); }
  }

  async function handleAddService() {
    if (!newCategoryName.trim()) return;
    try {
      const subs = newSubcategories.split(",").map(s => s.trim()).filter(Boolean);
      await addService(newCategoryName, newCategoryIcon, subs);
      await loadServicesAdmin();
      setNewCategoryName(""); setNewCategoryIcon("🔨"); setNewSubcategories("");
      setActionMsg("Service added!");
      setTimeout(() => setActionMsg(""), 3000);
    } catch (err) { console.error(err); }
  }

  async function handleDeleteService(categoryId: string) {
    try {
      await deleteService(categoryId);
      await loadServicesAdmin();
      setActionMsg("Service removed");
      setTimeout(() => setActionMsg(""), 3000);
    } catch (err) { console.error(err); }
  }

  async function loadAnalytics() {
    setAnalyticsLoading(true);
    try {
      const data = await getAnalytics();
      setAnalytics(data);
    } catch (err) { console.error("Analytics failed:", err); }
    finally { setAnalyticsLoading(false); }
  }

  async function loadSupportConvos() {
    try {
      const headers = await getAuthHeader();
      const res = await axios.get(`${awsConfig.apiUrl}/support/conversations`, { headers });
      setSupportConvos(res.data.conversations || []);
    } catch (err) { console.error("Failed to load support:", err); }
  }

  async function loadConvoMessages(conversationId: string) {
    try {
      const headers = await getAuthHeader();
      const res = await axios.get(`${awsConfig.apiUrl}/messages?conversationId=${conversationId}`, { headers });
      setConvoMessages(res.data.messages || []);
      setActiveConvo(conversationId);
    } catch (err) { console.error(err); }
  }

  async function sendSupportReply() {
    if (!supportReply.trim() || !activeConvo) return;
    const ws = adminWsRef.current;
    const userId = activeConvo.replace("support_", "");
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        action: "sendMessage",
        conversationId: activeConvo,
        message: supportReply,
        senderId: adminId,
        recipientId: userId
      }));
      setConvoMessages(prev => [...prev, {
        conversationId: activeConvo,
        sentAt: new Date().toISOString(),
        message: supportReply,
        senderId: adminId,
        recipientId: userId
      }]);
      setSupportReply("");
    }
  }

  async function handleUpdatePrice() {
    if (!editingPrice) return;
    try {
      await updateLeadPricing(editingPrice.category, editingPrice.jobSize, editingPrice.price, editingPrice.description);
      setActionMsg("Price updated successfully");
      setTimeout(() => setActionMsg(""), 3000);
      setEditingPrice(null);
      loadPricing();
    } catch {
      setActionMsg("Failed to update price");
    }
  }

  async function handleVerifyPro(proId: string, verified: boolean) {
    try {
      await adminVerifyPro(proId, verified);
      setActionMsg("Pro verified successfully");
      setTimeout(() => setActionMsg(""), 3000);
      loadUsers();
    } catch {
      setActionMsg("Action failed");
    }
  }

  async function handleSuspend(username: string, suspend: boolean) {
    try {
      await adminSuspendUser(username, suspend);
      setActionMsg(suspend ? "User suspended" : "User enabled");
      setTimeout(() => setActionMsg(""), 3000);
      loadUsers();
    } catch {
      setActionMsg("Action failed");
    }
  }

  const stats = {
    total:   users.length,
    pros:    users.filter(u => u.role === "pro").length,
    clients: users.filter(u => u.role === "client").length,
    admins:  users.filter(u => u.role === "admin").length,
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <div className="w-56 min-h-screen bg-white border-r border-gray-200 flex flex-col py-5 fixed left-0 top-0 bottom-0">
        <div className="px-5 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <svg width="32" height="32" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="adLg1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#4C6EF5"/><stop offset="100%" stopColor="#1A3CE8"/></linearGradient>
                <linearGradient id="adLg2" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#20C997"/><stop offset="100%" stopColor="#0E8C6A"/></linearGradient>
                <linearGradient id="adBg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#3B5BDB"/><stop offset="100%" stopColor="#0E8C6A"/></linearGradient>
              </defs>
              <rect width="52" height="52" rx="14" fill="url(#adBg)"/>
              <g transform="translate(4,5)"><path d="M11,0 C4.9,0 0,4.9 0,11 C0,17.1 11,28 11,28 C11,28 22,17.1 22,11 C22,4.9 17.1,0 11,0 Z" fill="white" opacity="0.95"/><circle cx="11" cy="10" r="5" fill="url(#adLg1)"/><circle cx="11" cy="10" r="2.5" fill="white"/></g>
              <g transform="translate(25,5)"><path d="M11,0 C4.9,0 0,4.9 0,11 C0,17.1 11,28 11,28 C11,28 22,17.1 22,11 C22,4.9 17.1,0 11,0 Z" fill="white" opacity="0.85"/><circle cx="11" cy="10" r="5" fill="url(#adLg2)"/><circle cx="11" cy="10" r="2.5" fill="white"/></g>
            </svg>
            <span className="font-display text-base font-bold text-navy">Mouna</span>
          </div>
          <div className="bg-gradient-to-r from-mouna-blue to-navy text-white rounded-xl px-3 py-2">
            <div className="text-xs font-bold">⚡ Admin Console</div>
            <div className="text-xs opacity-80">Curated Concierge</div>
          </div>
        </div>
        {NAV.map(item => (
          <button key={item} onClick={() => setActiveNav(item)}
            className={"text-left px-5 py-2.5 text-sm font-medium border-l-2 transition-all " +
              (activeNav === item
                ? "text-mouna-blue bg-blue-pale border-mouna-blue font-bold"
                : "text-gray-500 border-transparent hover:bg-gray-50")}>
            {item}
          </button>
        ))}
        <div className="mt-auto px-5 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-gray-500 font-bold">Appearance</span>
            <ThemeToggle />
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-navy flex items-center justify-center text-white text-xs font-bold">MA</div>
            <div>
              <div className="text-xs font-bold text-navy">Mouna Admin</div>
              <button onClick={handleSignOut} className="text-xs text-gray-400">Sign out</button>
            </div>
          </div>
        </div>
      </div>

      <div className="ml-56 flex-1 p-6">
        {actionMsg && (
          <div className="fixed top-4 right-4 bg-teal text-white rounded-xl px-4 py-3 text-sm font-bold shadow-lg z-50">
            {actionMsg}
          </div>
        )}

        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2 border border-gray-200 max-w-sm">
            <span className="text-gray-400 text-sm">search</span>
            <input type="text" placeholder="Search marketplace records..."
              className="bg-transparent text-sm outline-none flex-1 text-gray-600"/>
          </div>
          <div className="ml-auto">
            <div className="w-8 h-8 rounded-full bg-navy flex items-center justify-center text-white text-xs font-bold">MA</div>
          </div>
        </div>

        {activeNav === "Dashboard" && (
          <div>
            <div className="flex items-start justify-between mb-5">
              <div>
                <h1 className="font-display text-2xl font-bold text-navy">Marketplace Overview</h1>
                <p className="text-sm text-gray-400">Real-time performance metrics for Mouna.</p>
              </div>
              <button onClick={loadAnalytics} className="bg-blue-pale text-mouna-blue text-xs font-bold px-4 py-2 rounded-full hover:bg-mouna-blue hover:text-white transition-colors">↻ Refresh</button>
            </div>

            {analyticsLoading && (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-4 border-mouna-blue border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {analytics && !analyticsLoading && (
              <>
                {/* Overview cards */}
                <div className="grid grid-cols-2 gap-3 mb-5">
                  {[
                    { label: "Total Revenue", value: `$${analytics.overview.totalRevenue.toFixed(2)}`, color: "bg-gradient-to-br from-mouna-blue to-blue-900 text-white" },
                    { label: "Total Bookings", value: analytics.overview.totalBookings, color: "bg-white border border-gray-200" },
                    { label: "Active Pros", value: analytics.overview.totalPros, color: "bg-white border border-gray-200" },
                    { label: "Avg Rating", value: `⭐ ${analytics.overview.avgRating}`, color: "bg-white border border-gray-200" },
                    { label: "Acceptance Rate", value: `${analytics.overview.overallAcceptanceRate}%`, color: "bg-teal-light" },
                    { label: "Total Reviews", value: analytics.overview.totalReviews, color: "bg-white border border-gray-200" },
                  ].map(stat => (
                    <div key={stat.label} className={`rounded-2xl p-4 ${stat.color}`}>
                      <div className="text-xs opacity-70 uppercase tracking-wider mb-1">{stat.label}</div>
                      <div className="font-mono text-2xl font-medium">{stat.value}</div>
                    </div>
                  ))}
                </div>

                {/* Bookings last 7 days */}
                <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
                  <h2 className="font-bold text-navy mb-3">Bookings — Last 7 Days</h2>
                  <div className="flex items-end gap-2 h-24">
                    {analytics.last7Days.map((day: any) => {
                      const max = Math.max(...analytics.last7Days.map((d: any) => d.count), 1);
                      const height = Math.max((day.count / max) * 80, 4);
                      return (
                        <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                          <div className="text-xs text-navy font-bold">{day.count}</div>
                          <div className="w-full bg-mouna-blue rounded-t-lg" style={{height: `${height}px`}}></div>
                          <div className="text-xs text-gray-400">{day.date.slice(5)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Category breakdown */}
                <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
                  <h2 className="font-bold text-navy mb-3">Bookings by Category</h2>
                  {analytics.categoryBreakdown.slice(0, 6).map((cat: any) => {
                    const max = analytics.categoryBreakdown[0]?.count || 1;
                    return (
                      <div key={cat.category} className="mb-2">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-navy font-semibold">{cat.category}</span>
                          <span className="text-gray-400">{cat.count} bookings</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div className="bg-mouna-blue rounded-full h-2" style={{width: `${(cat.count/max)*100}%`}}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Top Pros */}
                {analytics.topPros.length > 0 && (
                  <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
                    <h2 className="font-bold text-navy mb-3">Top Performing Pros</h2>
                    {analytics.topPros.map((pro: any, i: number) => (
                      <div key={pro.name} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                        <div className="w-7 h-7 rounded-full bg-mouna-blue flex items-center justify-center text-white text-xs font-bold">
                          {i + 1}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-bold text-navy">{pro.name}</div>
                          <div className="text-xs text-gray-400">{pro.category} · ⭐ {pro.rating} · {pro.acceptanceRate}% accept</div>
                        </div>
                        <div className="text-xs font-bold text-mouna-blue">{pro.hireCount} hires</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Anti-Gaming Flags */}
            {flags.length > 0 && (
              <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-bold text-navy">🚨 Flags & Reports ({flags.length})</h2>
                  <button onClick={loadFlags} className="text-xs text-mouna-blue font-bold">Refresh</button>
                </div>
                {flags.map(flag => (
                  <div key={flag.flagId} className="border border-gray-100 rounded-xl p-3 mb-2">
                    <div className="flex items-start justify-between mb-1">
                      <div>
                        <div className={"text-xs font-bold rounded-full px-2 py-0.5 inline-block mb-1 " +
                          (flag.severity === "HIGH" ? "bg-red-100 text-red-600" :
                           flag.severity === "MEDIUM" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-500")}>
                          {flag.severity}
                        </div>
                        <div className="text-sm font-bold text-navy">{flag.reason}</div>
                        <div className="text-xs text-gray-400">{flag.proId?.slice(0,8)}... · {new Date(flag.createdAt).toLocaleDateString()}</div>
                      </div>
                      <button onClick={() => setSelectedFlag(selectedFlag?.flagId === flag.flagId ? null : flag)}
                        className="text-xs text-mouna-blue font-bold">
                        {selectedFlag?.flagId === flag.flagId ? "Close" : "Review"}
                      </button>
                    </div>
                    {selectedFlag?.flagId === flag.flagId && (
                      <div className="mt-3 border-t border-gray-100 pt-3">
                        <div className="bg-gray-50 rounded-lg p-2 text-xs text-gray-600 mb-3 font-mono">
                          {flag.details}
                        </div>
                        <input value={flagNote} onChange={e => setFlagNote(e.target.value)}
                          placeholder="Admin note (optional)..."
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs mb-2 outline-none"/>
                        <div className="flex gap-2 flex-wrap">
                          {["warn", "freeze", "reset_rating", "dismiss"].map(action => (
                            <button key={action} onClick={() => handleFlagAction(flag.flagId, flag.proId, action)}
                              className={"px-3 py-1.5 rounded-full text-xs font-bold " +
                                (action === "freeze" ? "bg-red-500 text-white" :
                                 action === "warn" ? "bg-yellow-500 text-white" :
                                 action === "reset_rating" ? "bg-mouna-blue text-white" :
                                 "bg-gray-100 text-gray-500")}>
                              {action.replace("_", " ")}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Recent Payments */}
                {analytics.recentPayments.length > 0 && (
                  <div className="bg-white rounded-2xl p-4 shadow-sm">
                    <h2 className="font-bold text-navy mb-3">Recent Payments</h2>
                    {analytics.recentPayments.map((payment: any, i: number) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                        <div>
                          <div className="text-xs font-semibold text-navy">{payment.description || "Lead fee"}</div>
                          <div className="text-xs text-gray-400">{formatDate(payment.date)}</div>
                        </div>
                        <div className="font-bold text-mouna-blue text-sm">${payment.amount.toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeNav === "Users" && (
          <div>
            <div className="flex items-start justify-between mb-5">
              <div>
                <h1 className="font-display text-2xl font-bold text-navy">User Management</h1>
                <p className="text-sm text-gray-400">Real users from your Cognito user pool.</p>
              </div>
              <button onClick={loadUsers} className="bg-blue-pale text-mouna-blue text-xs font-bold px-4 py-2 rounded-full hover:bg-mouna-blue hover:text-white transition-colors">↻ Refresh</button>
            </div>

            <div className="grid grid-cols-4 gap-4 mb-5">
              {[
                { label:"Total Users",   value: stats.total,   icon: "👥", color: "bg-blue-pale text-mouna-blue" },
                { label:"Clients",       value: stats.clients, icon: "🏠", color: "bg-teal-light text-teal" },
                { label:"Professionals", value: stats.pros,    icon: "🔨", color: "bg-gold-light text-gold" },
                { label:"Admins",        value: stats.admins,  icon: "🛡️", color: "bg-red-50 text-red-500" },
              ].map(s => (
                <div key={s.label} className="bg-white rounded-2xl p-4 shadow-card">
                  <div className={"w-9 h-9 rounded-xl flex items-center justify-center text-lg mb-3 " + s.color}>{s.icon}</div>
                  <div className="font-bold text-2xl text-navy mb-1">{s.value}</div>
                  <div className="text-xs text-gray-400">{s.label}</div>
                </div>
              ))}
            </div>

            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-mouna-blue border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 text-red-500 text-sm rounded-xl p-4 mb-4">{error}</div>
            )}

            {!loading && users.length > 0 && (
              <div className="bg-white rounded-2xl shadow-card overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      {["USER","ROLE","STATUS","JOINED","ACTIONS"].map(h => (
                        <th key={h} className="text-left text-xs text-gray-400 uppercase tracking-wider px-4 py-3 font-bold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr key={user.sub} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-blue-pale flex items-center justify-center text-xs font-bold text-mouna-blue">
                              {user.email[0].toUpperCase()}
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-navy">{user.email}</div>
                              <div className="text-xs text-gray-400">{user.sub.slice(0,8)}...</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={"text-xs font-bold rounded-full px-2 py-1 capitalize " +
                            (user.role === "pro" ? "bg-blue-pale text-mouna-blue" :
                             user.role === "admin" ? "bg-yellow-50 text-yellow-600" :
                             "bg-gray-100 text-gray-500")}>
                            {user.role}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={"text-xs font-bold rounded-full px-2 py-1 " +
                            (user.enabled ? "bg-teal-light text-teal" : "bg-red-50 text-red-500")}>
                            {user.enabled ? "Active" : "Suspended"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {new Date(user.created).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            {user.role === "pro" && (
                              <button onClick={() => handleVerifyPro(user.sub, true)}
                                className="bg-teal-light text-teal text-xs font-bold rounded-lg px-2 py-1 hover:opacity-80">
                                Verify
                              </button>
                            )}
                            <button onClick={() => handleSuspend(user.sub, user.enabled)}
                              className={"text-xs font-bold rounded-lg px-2 py-1 hover:opacity-80 " +
                                (user.enabled ? "bg-red-50 text-red-500" : "bg-teal-light text-teal")}>
                              {user.enabled ? "Suspend" : "Enable"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && users.length === 0 && !error && (
              <div className="text-center py-12">
                <div className="font-bold text-navy">No users loaded</div>
                <div className="text-xs text-gray-400 mt-1">Click Refresh to load users from Cognito</div>
              </div>
            )}
          </div>
        )}

        {activeNav === "Pricing" && (
          <div>
            <div className="flex items-start justify-between mb-5">
              <div>
                <h1 className="font-display text-2xl font-bold text-navy">Lead Pricing</h1>
                <p className="text-sm text-gray-400">Adjust lead prices per service category and job size.</p>
              </div>
              <button onClick={loadPricing} className="bg-blue-pale text-mouna-blue text-xs font-bold px-4 py-2 rounded-full hover:bg-mouna-blue hover:text-white transition-colors">↻ Refresh</button>
            </div>

            {pricingLoading && (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-4 border-mouna-blue border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {editingPrice && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4">
                  <h2 className="font-bold text-navy text-lg mb-4">Edit Price</h2>
                  <div className="mb-3">
                    <div className="text-xs text-gray-400 mb-1">Category</div>
                    <div className="font-semibold text-navy">{editingPrice.category}</div>
                  </div>
                  <div className="mb-3">
                    <div className="text-xs text-gray-400 mb-1">Job Size</div>
                    <div className="font-semibold text-navy">{editingPrice.jobSize}</div>
                  </div>
                  <div className="mb-3">
                    <label className="text-xs text-gray-400 font-semibold mb-1 block">Price ($)</label>
                    <input
                      type="number"
                      value={editingPrice.price}
                      onChange={e => setEditingPrice({...editingPrice, price: Number(e.target.value)})}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm outline-none focus:border-mouna-blue"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="text-xs text-gray-400 font-semibold mb-1 block">Description</label>
                    <input
                      type="text"
                      value={editingPrice.description}
                      onChange={e => setEditingPrice({...editingPrice, description: e.target.value})}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm outline-none focus:border-mouna-blue"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setEditingPrice(null)} className="flex-1 bg-gray-100 text-gray-500 rounded-xl py-2 text-sm font-semibold">Cancel</button>
                    <button onClick={handleUpdatePrice} className="flex-1 bg-mouna-blue text-white rounded-xl py-2 text-sm font-bold">Save</button>
                  </div>
                </div>
              </div>
            )}

            {!pricingLoading && pricing.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      {["CATEGORY","JOB SIZE","DESCRIPTION","PRICE","ACTION"].map(h => (
                        <th key={h} className="text-left text-xs text-gray-400 uppercase tracking-wider px-4 py-3 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pricing.sort((a,b) => a.category.localeCompare(b.category)).map(item => (
                      <tr key={item.category+item.jobSize} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-semibold text-navy">{item.category}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{item.jobSize}</td>
                        <td className="px-4 py-3 text-xs text-gray-400">{item.description}</td>
                        <td className="px-4 py-3">
                          <span className="font-bold text-mouna-blue">${Number(item.price).toFixed(0)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setEditingPrice({category: item.category, jobSize: item.jobSize, price: Number(item.price), description: item.description})}
                            className="bg-blue-pale text-mouna-blue text-xs font-bold rounded-lg px-3 py-1 hover:opacity-80"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeNav === "Support" && (
          <div>
            <h1 className="font-display text-2xl font-bold text-navy mb-2">Support Inbox</h1>
            <p className="text-sm text-gray-400 mb-5">Real-time conversations with clients and pros.</p>
            <div className="flex gap-4" style={{height: "500px"}}>
              <div className="w-64 bg-white rounded-2xl shadow-sm overflow-y-auto flex-shrink-0">
                {supportConvos.length === 0 && (
                  <div className="text-center py-8 text-sm text-gray-400">No support conversations yet</div>
                )}
                {supportConvos.map(convo => (
                  <button key={convo.conversationId}
                    onClick={() => loadConvoMessages(convo.conversationId)}
                    className={"w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 " + (activeConvo === convo.conversationId ? "bg-blue-pale" : "")}>
                    <div className="text-xs font-bold text-navy truncate">{convo.userId.slice(0, 12)}...</div>
                    <div className="text-xs text-gray-400 truncate mt-0.5">{convo.lastMessage?.message || "No messages"}</div>
                    <div className="text-xs text-gray-300 mt-0.5">{convo.messageCount} messages</div>
                  </button>
                ))}
              </div>
              <div className="flex-1 bg-white rounded-2xl shadow-sm flex flex-col overflow-hidden">
                {!activeConvo && (
                  <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Select a conversation</div>
                )}
                {activeConvo && (
                  <>
                    <div className="flex-1 overflow-y-auto p-4 space-y-2">
                      {convoMessages.map((msg, i) => (
                        <div key={i} className={"flex " + (msg.senderId === adminId ? "justify-end" : "justify-start")}>
                          <div className={"px-3 py-2 rounded-xl text-xs max-w-xs " + (msg.senderId === adminId ? "bg-mouna-blue text-white" : "bg-gray-100 text-navy")}>
                            {msg.message}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-gray-100 p-3 flex gap-2">
                      <input type="text" value={supportReply}
                        onChange={e => setSupportReply(e.target.value)}
                        onKeyPress={e => e.key === "Enter" && sendSupportReply()}
                        placeholder="Type a reply..."
                        className="flex-1 border border-gray-200 rounded-full px-3 py-1.5 text-xs outline-none"/>
                      <button onClick={sendSupportReply}
                        className="bg-mouna-blue text-white rounded-full px-4 py-1.5 text-xs font-bold">Send</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {activeNav === "Disputes" && (
          <div>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h1 className="font-display text-2xl font-bold text-navy">Disputes</h1>
                <p className="text-sm text-gray-400">{disputes.filter(d => d.status === "open").length} open disputes</p>
              </div>
              <button onClick={loadDisputes} className="bg-blue-pale text-mouna-blue text-xs font-bold px-4 py-2 rounded-full hover:bg-mouna-blue hover:text-white transition-colors">↻ Refresh</button>
            </div>

            {resolvingDispute && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4">
                  <h2 className="font-bold text-navy text-lg mb-1">Resolve Dispute</h2>
                  <p className="text-xs text-gray-400 mb-4">{resolvingDispute.reason}</p>
                  <div className="mb-3">
                    <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1 block">Resolution</label>
                    <textarea value={disputeResolution} onChange={e => setDisputeResolution(e.target.value)} rows={3}
                      placeholder="Explain the resolution to the user..."
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none resize-none"/>
                  </div>
                  <div className="mb-4">
                    <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1 block">Admin Notes (private)</label>
                    <textarea value={disputeNotes} onChange={e => setDisputeNotes(e.target.value)} rows={2}
                      placeholder="Internal notes..."
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none resize-none"/>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setResolvingDispute(null)} className="flex-1 bg-gray-100 text-gray-500 rounded-xl py-2 text-sm">Cancel</button>
                    <button onClick={() => handleResolveDispute("resolved")} className="flex-1 bg-teal text-white rounded-xl py-2 text-sm font-bold">Resolve</button>
                    <button onClick={() => handleResolveDispute("closed")} className="flex-1 bg-gray-500 text-white rounded-xl py-2 text-sm font-bold">Close</button>
                  </div>
                </div>
              </div>
            )}

            {/* Tickets section */}
            <div className="mb-6">
              <h2 className="font-bold text-navy mb-3">Support Tickets ({tickets.filter(t => t.status !== "resolved" && t.status !== "closed").length} open)</h2>
              {tickets.length === 0 && <div className="text-sm text-gray-400 mb-4">No tickets yet</div>}
              {tickets.map(ticket => (
                <div key={ticket.ticketId} className="bg-white rounded-2xl p-4 shadow-sm mb-3">
                  <div className="flex items-start justify-between mb-2" onClick={() => setSelectedTicket(selectedTicket?.ticketId === ticket.ticketId ? null : ticket)} style={{cursor:"pointer"}}>
                    <div>
                      <div className="font-bold text-navy text-sm">{ticket.issueType}</div>
                      <div className="text-xs text-gray-400">{ticket.userEmail} · {formatDate(ticket.createdAt)}</div>
                    </div>
                    <span className={`text-xs font-bold rounded-full px-3 py-1 capitalize ${
                      ticket.status === "open" ? "bg-yellow-100 text-yellow-700" :
                      ticket.status === "escalated" ? "bg-red-100 text-red-600" :
                      ticket.status === "resolved" ? "bg-green-100 text-teal" : "bg-blue-100 text-mouna-blue"
                    }`}>{ticket.status}</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-2">{ticket.description}</p>
                  {ticket.evidenceUrls?.length > 0 && (
                    <div className="flex gap-2 mb-2">
                      {ticket.evidenceUrls.map((url: string, i: number) => (
                        <SecureImage key={i} src={url} alt="Evidence" className="w-16 h-16 object-cover rounded-lg"/>
                      ))}
                    </div>
                  )}
                  {selectedTicket?.ticketId === ticket.ticketId && (
                  <div className="flex gap-2 mt-3">
                    <button onClick={e => { e.stopPropagation(); setSelectedTicket(ticket); setTicketReply(""); setRequestType("reply"); }}
                      className="bg-mouna-blue text-white text-xs font-bold rounded-full px-4 py-2">
                      Manage Ticket
                    </button>
                  </div>
                  )}
                </div>
              ))}
            </div>

            {/* Ticket Management Modal */}
            {selectedTicket && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl w-full max-w-lg max-h-screen overflow-y-auto">
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-bold text-navy text-lg">{selectedTicket.issueType}</h2>
                      <button onClick={() => setSelectedTicket(null)} className="text-gray-400 text-xl">✕</button>
                    </div>
                    <div className="text-xs text-gray-400 mb-3">{selectedTicket.userEmail} · {new Date(selectedTicket.createdAt).toLocaleDateString()}</div>
                    <div className="bg-gray-50 rounded-xl p-3 mb-4">
                      <div className="text-xs font-bold text-navy mb-1">Description</div>
                      <div className="text-xs text-gray-600">{selectedTicket.description}</div>
                    </div>
                    {selectedTicket.evidenceUrls?.length > 0 && (
                      <div className="mb-4">
                        <div className="text-xs font-bold text-navy mb-2">Evidence</div>
                        <div className="flex gap-2 flex-wrap">
                          {selectedTicket.evidenceUrls.map((url: string, i: number) => (
                            <SecureImage key={i} src={url} alt="Evidence" className="w-20 h-20 object-cover rounded-xl"/>
                          ))}
                        </div>
                      </div>
                    )}
                    {(selectedTicket.messages || []).length > 0 && (
                      <div className="mb-4">
                        <div className="text-xs font-bold text-navy mb-2">Message Thread</div>
                        {selectedTicket.messages.map((msg: any, i: number) => (
                          <div key={i} className="bg-gray-50 rounded-xl p-3 mb-2">
                            <div className="text-xs font-bold text-navy mb-1 capitalize">{msg.senderRole === "admin" ? "You (Admin)" : msg.senderRole}</div>
                            <div className="text-xs text-gray-600">{msg.message}</div>
                            <div className="text-xs text-gray-400 mt-1">{new Date(msg.sentAt).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2 mb-3">
                      {["reply", "more_info", "pro_response"].map(type => (
                        <button key={type} onClick={() => setRequestType(type)}
                          className={"px-3 py-1 rounded-full text-xs font-bold " +
                            (requestType === type ? "bg-mouna-blue text-white" : "bg-gray-100 text-gray-500")}>
                          {type === "reply" ? "Reply" : type === "more_info" ? "Request Info" : "Ask Pro"}
                        </button>
                      ))}
                    </div>
                    <select value={estimatedTime} onChange={e => setEstimatedTime(e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs outline-none mb-3">
                      <option>within 24-48 hours</option>
                      <option>within 2-3 days</option>
                      <option>within 1 week</option>
                      <option>immediately</option>
                    </select>
                    <textarea value={ticketReply} onChange={e => setTicketReply(e.target.value)}
                      placeholder={requestType === "more_info" ? "What info do you need from the client?" : requestType === "pro_response" ? "Message to send to the pro..." : "Type your reply..."}
                      rows={3} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-mouna-blue resize-none mb-3"/>
                    <div className="flex gap-2">
                      <button onClick={() => handleTicketReply(selectedTicket.ticketId, "in_review")}
                        className="flex-1 bg-mouna-blue text-white rounded-full py-3 text-sm font-bold">Send</button>
                      <button onClick={() => handleTicketReply(selectedTicket.ticketId, "resolved")}
                        className="flex-1 bg-teal text-white rounded-full py-3 text-sm font-bold">Resolve</button>
                      <button onClick={() => handleTicketReply(selectedTicket.ticketId, "escalated")}
                        className="flex-1 bg-red-500 text-white rounded-full py-3 text-sm font-bold">Escalate</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <h2 className="font-bold text-navy mb-3">Formal Disputes</h2>
            {disputes.length === 0 && (
              <div className="text-center py-12">
                <div className="text-4xl mb-3">⚖️</div>
                <div className="font-bold text-navy">No disputes yet</div>
              </div>
            )}

            <div className="space-y-3">
              {disputes.map(dispute => (
                <div key={dispute.disputeId} className="bg-white rounded-2xl p-4 shadow-sm">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-bold text-navy text-sm">{dispute.reason}</div>
                      <div className="text-xs text-gray-400">{dispute.userEmail} · {new Date(dispute.createdAt).toLocaleDateString()}</div>
                    </div>
                    <span className={`text-xs font-bold rounded-full px-3 py-1 capitalize ${
                      dispute.status === "open" ? "bg-yellow-100 text-yellow-700" :
                      dispute.status === "resolved" ? "bg-green-100 text-teal" : "bg-gray-100 text-gray-500"
                    }`}>{dispute.status}</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-3">{dispute.description}</p>
                  {dispute.resolution && (
                    <div className="bg-teal-light rounded-xl p-3 mb-3">
                      <div className="text-xs font-bold text-teal mb-1">Resolution</div>
                      <div className="text-xs text-gray-600">{dispute.resolution}</div>
                    </div>
                  )}
                  {dispute.status === "open" && (
                    <button onClick={() => { setResolvingDispute(dispute); setDisputeResolution(""); setDisputeNotes(""); }}
                      className="bg-mouna-blue text-white text-xs font-bold rounded-full px-4 py-2">
                      Resolve →
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeNav === "Settings" && (
          <SettingsTab
            services={services}
            servicesLoading={servicesLoading}
            newCategoryName={newCategoryName}
            setNewCategoryName={setNewCategoryName}
            newCategoryIcon={newCategoryIcon}
            setNewCategoryIcon={setNewCategoryIcon}
            newSubcategories={newSubcategories}
            setNewSubcategories={setNewSubcategories}
            handleAddService={handleAddService}
            handleDeleteService={handleDeleteService}
            updateService={updateService}
            loadServicesAdmin={loadServicesAdmin}
          />
        )}

        {!["Dashboard","Users","Pricing","Support","Disputes","Settings"].includes(activeNav) && (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="font-bold text-navy">{activeNav}</div>
              <div className="text-sm text-gray-400 mt-1">Coming soon</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
